/**
 * Binary container format for the Organized Estate Secure Information File (.oesif).
 *
 * Format v1 layout:
 *   Offset  Length  Field
 *   0       4       Magic "OESF" (0x4F 0x45 0x53 0x46)
 *   4       1       Format version (0x01)
 *   5       16      Salt — random, per-file, for PBKDF2 key derivation
 *   21      12      IV  — random, per-file, for AES-GCM
 *   33      4       KDF iterations (uint32 big-endian)
 *   37      8       Last-modified timestamp (int64 big-endian, Unix seconds)
 *   45      4       Encrypted body length (uint32 big-endian)
 *   49+     varies  Encrypted body (JSON-encoded SecureFileBody + 16-byte GCM tag)
 *
 * ---
 * AES-GCM auth failure disambiguation:
 *
 * crypto.subtle.decrypt with AES-GCM throws a generic OperationError on ANY
 * authentication failure — wrong key, tampered ciphertext, or truncated input.
 * The Web Crypto API cannot distinguish between these cases natively.
 *
 * Decision (Phase 1): wrong passphrase AND tampered ciphertext both surface as
 * InvalidPassphraseError, with a message that acknowledges corruption possibility.
 * When decryption SUCCEEDS but the body is not valid JSON, CorruptedFileError is
 * thrown — auth-tag success means the passphrase is correct, so the file itself
 * is damaged. CorruptedFileError remains reserved for other positive-identification
 * cases (e.g. a checksum computed independently of AES-GCM).
 * ---
 */

import { deriveKey, encryptBody, decryptBody } from "./crypto";
import { CorruptedFileError, InvalidPassphraseError, MalformedContainerError } from "./errors";
import {
  FILE_MAGIC_BYTES,
  HEADER_SIZE,
  MAX_ITERATIONS,
  MAX_KNOWN_VERSION,
  MIN_ITERATIONS,
  OFFSET_BODY,
  OFFSET_BODY_LEN,
  OFFSET_ITERATIONS,
  OFFSET_IV,
  OFFSET_MAGIC,
  OFFSET_SALT,
  OFFSET_TIMESTAMP,
  OFFSET_VERSION,
  PBKDF2_ITERATIONS,
} from "./constants";
import type { BuildContainerInput, ParsedContainer } from "./types";

/** Internal body structure encrypted inside the container. Never serialized externally. */
interface SecureFileBody {
  version: 1;
  data: unknown;
  plaintext: string;
}

export async function buildContainer(input: BuildContainerInput): Promise<Uint8Array> {
  const { data, plaintext, passphrase } = input;

  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const iv = new Uint8Array(12);
  crypto.getRandomValues(iv);

  const key = await deriveKey(passphrase, salt, PBKDF2_ITERATIONS);

  const body: SecureFileBody = { version: 1, data, plaintext };
  const bodyBytes = new TextEncoder().encode(JSON.stringify(body));
  const encryptedBody = await encryptBody(key, iv, bodyBytes);

  const timestamp = BigInt(Math.floor(Date.now() / 1000));

  const container = new Uint8Array(HEADER_SIZE + encryptedBody.length);
  const view = new DataView(container.buffer);

  // Magic
  container[OFFSET_MAGIC]     = 0x4f;
  container[OFFSET_MAGIC + 1] = 0x45;
  container[OFFSET_MAGIC + 2] = 0x53;
  container[OFFSET_MAGIC + 3] = 0x46;

  // Version
  container[OFFSET_VERSION] = 1;

  // Salt + IV
  container.set(salt, OFFSET_SALT);
  container.set(iv, OFFSET_IV);

  // Iterations
  view.setUint32(OFFSET_ITERATIONS, PBKDF2_ITERATIONS, false);

  // Timestamp (int64 big-endian)
  view.setBigInt64(OFFSET_TIMESTAMP, timestamp, false);

  // Body length
  view.setUint32(OFFSET_BODY_LEN, encryptedBody.length, false);

  // Body
  container.set(encryptedBody, OFFSET_BODY);

  return container;
}

export async function parseContainer<T = unknown>(
  bytes: Uint8Array,
  passphrase: string,
): Promise<ParsedContainer<T>> {
  // Minimum size guard
  if (bytes.length < HEADER_SIZE) {
    throw new MalformedContainerError(
      `File too small: ${bytes.length} bytes (minimum ${HEADER_SIZE})`,
    );
  }

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  // Magic
  const magic = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (magic !== FILE_MAGIC_BYTES) {
    throw new MalformedContainerError(
      `Invalid magic bytes: expected "${FILE_MAGIC_BYTES}", got "${magic}"`,
    );
  }

  // Version
  const version = bytes[OFFSET_VERSION];
  if (version > MAX_KNOWN_VERSION) {
    throw new MalformedContainerError("Unknown format version");
  }

  // Salt + IV
  const salt = bytes.slice(OFFSET_SALT, OFFSET_IV);
  const iv = bytes.slice(OFFSET_IV, OFFSET_ITERATIONS);

  // Iterations
  const iterations = view.getUint32(OFFSET_ITERATIONS, false);
  if (iterations > MAX_ITERATIONS) {
    throw new MalformedContainerError("Iteration count exceeds maximum");
  }
  if (iterations < MIN_ITERATIONS) {
    throw new MalformedContainerError(
      `Iteration count below minimum: file specifies ${iterations}, minimum is ${MIN_ITERATIONS}`,
    );
  }

  // Timestamp
  const timestampSeconds = Number(view.getBigInt64(OFFSET_TIMESTAMP, false));

  // Declared body length vs actual
  const bodyLength = view.getUint32(OFFSET_BODY_LEN, false);
  if (bytes.length !== HEADER_SIZE + bodyLength) {
    throw new MalformedContainerError(
      `Declared body length (${bodyLength}) does not match actual data (${bytes.length - HEADER_SIZE} bytes)`,
    );
  }

  const encryptedBody = bytes.slice(OFFSET_BODY, OFFSET_BODY + bodyLength);

  // Decrypt
  const key = await deriveKey(passphrase, salt, iterations);
  let bodyBytes: Uint8Array;
  try {
    bodyBytes = await decryptBody(key, iv, encryptedBody);
  } catch {
    throw new InvalidPassphraseError();
  }

  // Parse JSON — decryption success means passphrase is correct; non-JSON means corruption
  let body: SecureFileBody;
  try {
    body = JSON.parse(new TextDecoder().decode(bodyBytes)) as SecureFileBody;
  } catch {
    throw new CorruptedFileError(
      "File decrypted successfully but the body is not valid JSON. The file may be damaged.",
    );
  }

  if (body.version !== 1) {
    throw new MalformedContainerError(
      `Body version mismatch: expected 1, got ${body.version}`,
    );
  }

  return {
    data: body.data as T,
    plaintext: body.plaintext,
    lastModified: new Date(timestampSeconds * 1000),
    version: body.version,
  };
}
